"use client";

import { useMemo, useState } from "react";
import type { SortingState, ColumnVisibilityState } from "@tanstack/react-table";
import {
  type BrowseViewMode,
  type ListingIntentFilter,
  type PriceRange,
  type SpecFilter,
} from "./listing-browse-types";

export interface ListingFilterState {
  productType: string | null;
  conditions: string[];
  priceRange: PriceRange;
  specFilters: SpecFilter[];
  stateCode: string | null;
  listedAfterDays: number | null;
  intentFilter: ListingIntentFilter;
  searchQuery: string;
}

export interface ListingViewState {
  sorting: SortingState;
  columnVisibility: ColumnVisibilityState;
  mapVisible: boolean;
  viewMode: BrowseViewMode;
  advancedFiltersExpanded: boolean;
}

export type ListingBrowseState = ListingFilterState & ListingViewState;

const DEFAULT_TABLE_SORTING: SortingState = [];

const DEFAULT_COLUMN_VISIBILITY: ColumnVisibilityState = {
  productType: false,
  qty: false,
  zip: false,
};

export function useListingFilterState(initial?: Partial<ListingFilterState>) {
  const [productType, setProductType] = useState<string | null>(initial?.productType ?? null);
  const [conditions, setConditions] = useState<string[]>(initial?.conditions ?? []);
  const [priceRange, setPriceRange] = useState<PriceRange>(initial?.priceRange ?? { min: null, max: null });
  const [specFilters, setSpecFilters] = useState<SpecFilter[]>(initial?.specFilters ?? []);
  const [stateCode, setStateCode] = useState<string | null>(initial?.stateCode ?? null);
  const [listedAfterDays, setListedAfterDays] = useState<number | null>(initial?.listedAfterDays ?? null);
  const [intentFilter, setIntentFilter] = useState<ListingIntentFilter>(initial?.intentFilter ?? "all");
  const [searchQuery, setSearchQuery] = useState<string>(initial?.searchQuery ?? "");

  return useMemo(
    () => ({
      productType, setProductType, conditions, setConditions,
      priceRange, setPriceRange, specFilters, setSpecFilters,
      stateCode, setStateCode, listedAfterDays, setListedAfterDays,
      intentFilter, setIntentFilter, searchQuery, setSearchQuery,
    }),
    [productType, conditions, priceRange, specFilters, stateCode, listedAfterDays, intentFilter, searchQuery],
  );
}

export function useListingViewState(initial?: Partial<ListingViewState>) {
  const [sorting, setSorting] = useState<SortingState>(initial?.sorting ?? DEFAULT_TABLE_SORTING);
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(initial?.columnVisibility ?? DEFAULT_COLUMN_VISIBILITY);
  const [mapVisible, setMapVisible] = useState(initial?.mapVisible ?? true);
  const [viewMode, setViewMode] = useState<BrowseViewMode>(initial?.viewMode ?? "grid");
  const [advancedFiltersExpanded, setAdvancedFiltersExpanded] = useState(initial?.advancedFiltersExpanded ?? false);

  return useMemo(
    () => ({
      sorting, setSorting,
      columnVisibility, setColumnVisibility, mapVisible, setMapVisible,
      viewMode, setViewMode, advancedFiltersExpanded, setAdvancedFiltersExpanded,
    }),
    [sorting, columnVisibility, mapVisible, viewMode, advancedFiltersExpanded],
  );
}

export const DEFAULT_LISTING_FILTERS: ListingFilterState = {
  productType: null,
  conditions: [],
  priceRange: { min: null, max: null },
  specFilters: [],
  stateCode: null,
  listedAfterDays: null,
  intentFilter: "all",
  searchQuery: "",
};
